/**
 * Shared Analytics Data Cache
 * Provides caching layer for Firestore queries to prevent duplicate data fetching
 */

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes (same as hooks cache)

// Cache storage
const dataCache = new Map();

/**
 * Generate cache key for a collection query
 * @param {string} collection - Collection name
 * @param {string} period - Time period
 * @returns {string} - Cache key
 */
const getCacheKey = (collection, period) => {
  return `collection_${collection}_${period}`;
};

/**
 * Check if cache entry is still valid
 * @param {number} timestamp - Cache timestamp
 * @returns {boolean} - True if cache is valid
 */
const isCacheValid = (timestamp) => {
  return Date.now() - timestamp < CACHE_DURATION;
};

/**
 * Get cached collection data if available and valid
 * @param {string} collection - Collection name
 * @param {string} period - Time period
 * @returns {Array|null} - Cached data or null
 */
export const getCachedCollectionData = (collection, period) => {
  const cacheKey = getCacheKey(collection, period);
  const cached = dataCache.get(cacheKey);
  
  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }
  
  return null;
};

/**
 * Store collection data in cache
 * @param {string} collection - Collection name
 * @param {string} period - Time period
 * @param {Array} data - Data to cache
 */
export const setCachedCollectionData = (collection, period, data) => {
  const cacheKey = getCacheKey(collection, period);
  dataCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Clear cache for a specific collection and period
 * @param {string} collection - Collection name
 * @param {string} period - Time period
 */
export const clearCachedCollectionData = (collection, period) => {
  const cacheKey = getCacheKey(collection, period);
  dataCache.delete(cacheKey);
};

/**
 * Clear all cached data
 */
export const clearAllCachedData = () => {
  dataCache.clear();
};

